import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import SimpleBar from "simplebar-react";
import "simplebar-react/dist/simplebar.min.css";
import logo from "../../assets/logo.png";
import sentrioLogo from "../../assets/sentrio-logo.png";
import { fetchAdminMenu } from "../../slice/admin/adminMenuSlice";
import PortalIcon from "./component/PortalIcons";

/* matches middlewares/adminAuth.js and Store/Stores.jsx */
const SUPERADMIN_ROLE = 1;

/* ─── All menu items with their permission key ────────────────────────────── */
const ALL_MENU_ITEMS = [
  {
    key: "dashboard",
    permissionKey: "dashboard",
    title: "Dashboard",
    icon: "home",
    path: "/admin/dashboard",
    exact: true,
    hasSubmenu: false,
  },
  {
    key: "category",
    permissionKey: "category",
    title: "Category",
    icon: "package",
    path: "/admin/category",
    exact: true,
    hasSubmenu: false,
  },
  {
    key: "subcategory",
    permissionKey: "subCategory",
    title: "Sub Category",
    icon: "layers",
    path: "/admin/subcategory",
    exact: true,
    hasSubmenu: false,
  },
 {
  key: "products",
  permissionKey: "products",
  title: "Product",
  icon: "shopping-bag",
  path: "/admin/products",
  hasSubmenu: true,
  activePaths: ["/admin/products", "/admin/products/stockentry", "/admin/products/imageUpload"],
  subItems: [
    { title: "Item Master",  path: "/admin/products",             exact: true },
    { title: "Stock Entry",  path: "/admin/products/stockentry",  exact: true },
    { title: "Image Upload", path: "/admin/products/imageUpload", exact: true },
  ],
},
  {
    /* Both /api/bulk calls are superAdminOnly — allocation decides what every
       store sells. A scoped admin holding the products permission would see
       this row and get nothing but 403s, so the rank is checked as well. */
    key: "bulkallocate",
    permissionKey: "products",
    superAdminOnly: true,
    title: "Bulk Allocate",
    icon: "layers",
    path: "/admin/bulk-allocate",
    exact: true,
    hasSubmenu: false,
  },
  {
    /* Deliberately NOT superAdminOnly, unlike Bulk Allocate above. A stock
       take is the person standing in front of the shelf, and per-store stock
       exists precisely so a store manager owns their own count — the router
       pins them to their store server-side (scopeToStore + requireWriteScope),
       so a scoped admin gets their own shelf here, not a 403. */
    key: "bulkstock",
    permissionKey: "products",
    title: "Bulk Stock",
    icon: "layers",
    path: "/admin/bulk-stock",
    exact: true,
    hasSubmenu: false,
  },
  {
    /* Estate-wide by design — prices are the same in every store, so this
       edits the whole chain at once and is role 1 only, like Bulk Allocate.
       A store-bound admin holding the products permission would see the row
       and get nothing but 403s, so the rank is checked here too. */
    key: "bulkprice",
    permissionKey: "products",
    superAdminOnly: true,
    title: "Bulk Price",
    icon: "percent",
    path: "/admin/bulk-price",
    exact: true,
    hasSubmenu: false,
  },
  {
    /* Counter billing. Sits above Orders because it is the screen a cashier
       lives in all day, and the one they must be able to reach without
       hunting. */
    key: "pos",
    permissionKey: "orders",
    title: "Billing (POS)",
    icon: "receipt",
    path: "/admin/pos",
    exact: true,
    hasSubmenu: false,
  },
  {
    key: "billdesign",
    permissionKey: "billdesign",
    title: "Bill Designer",
    icon: "receipt",
    path: "/admin/bill-design",
    exact: true,
    hasSubmenu: false,
  },
  {
    key: "orders",
    permissionKey: "orders",
    title: "Orders",
    icon: "box",
    path: "/admin/order/recevied",
    exact: true,
    hasSubmenu: false,
  },
  /* Delivery Management (the single Location geofence) retired 2026-08-20 —
     each store now carries its own delivery area on the Stores page. */
  {
    key: "staff",
    title: "Staff",
    icon: "users",
    path: "/admin/staff",
    exact: true,
    hasSubmenu: false,
  },
  {
    key: "liveview",
    title: "Live View",
    icon: "video",
    path: "/admin/live-view",
    exact: true,
    hasSubmenu: false,
  },
  {
    /* This used to borrow the permission key of the removed kit menu.
       That key can no longer be granted anywhere, so Milling Slots now
       stands on its own — visible to any admin, like Staff and Live View. */
    key: "milling",
    title: "Milling Slots",
    icon: "mill",
    path: "/admin/milling/slots",
    exact: true,
    hasSubmenu: false,
  },
  {
    key: "taxmaster",
    permissionKey: "taxmaster",
    title: "Tax Master",
    icon: "percent",
    path: "/admin/taxmaster/list",
    exact: true,
    hasSubmenu: false,
  },
  {
    key: "gstreport",
    permissionKey: "taxmaster",
    title: "GST Report",
    icon: "file-text",
    path: "/admin/gst-report",
    exact: true,
    hasSubmenu: false,
  },

  {
    key: "offer",
    permissionKey: "offer",
    title: "Offer Management",
    icon: "tag",
    path: "/admin/offer/list",
    exact: true,
    hasSubmenu: false,
  },
  {
    key: "customer",
    permissionKey: "customer",
    title: "Customer",
    icon: "customer",
    path: "/admin/customer",
    exact: true,
    hasSubmenu: false,
  },
  {
    key: "loyalty",
    title: "Loyalty & Reelo",
    icon: "award",
    path: "/admin/loyalty",
    exact: true,
    hasSubmenu: false,
  },
  {
    key: "support",
    title: "Support",
    icon: "life-buoy",
    path: "/admin/support",
    hasSubmenu: false,
  },
  {
    key: "banner",
    permissionKey: "banner",
    title: "Banner",
    icon: "image",
    path: "/admin/banner",
    exact: true,
    hasSubmenu: false,
  },
  {
    key: "branding",
    permissionKey: "banner",
    title: "App Branding",
    icon: "palette",
    path: "/admin/branding",
    exact: true,
    hasSubmenu: false,
  },
  {
    key: "emailsettings",
    permissionKey: "notification",
    title: "Order Emails",
    icon: "mail",
    path: "/admin/settings/email",
    exact: true,
    hasSubmenu: false,
  },
  {
    key: "notification",
    permissionKey: "notification",
    title: "Notification",
    icon: "bell",
    path: "/admin/notification",
    exact: true,
    hasSubmenu: false,
  },
  {
    key: "stores",
    permissionKey: "settings",
    title: "Stores",
    icon: "map-pin",
    path: "/admin/stores",
    exact: true,
    hasSubmenu: false,
  },
  {
    key: "storesettings",
    permissionKey: "settings",
    title: "Store Settings",
    icon: "sliders",
    path: "/admin/store/settings",
    exact: true,
    hasSubmenu: false,
  },
  {
    /* Visible to any admin — the read API is guarded by requireAdmin. */
    key: "audit",
    title: "Audit Log",
    icon: "shield",
    path: "/admin/audit",
    exact: true,
    hasSubmenu: false,
  },
  {
    key: "settings",
    permissionKey: "settings",
    title: "Settings",
    icon: "settings",
    path: "/admin/settings",
    hasSubmenu: true,
    activePaths: ["/admin/settings/Users", "/admin/settings/Permission", "/admin/settings/keys", "/admin/settings"],
    subItems: [
      { title: "Add Users",  path: "/admin/settings/Users",      exact: true },
      { title: "Keys & Secrets", path: "/admin/settings/keys", exact: true },
      { title: "Permission", path: "/admin/settings/Permission", exact: true },
    ],
  }
];
/* ─── Nav design system ──────────────────────────────────────────────────────
   A rail of sections and a panel of items. Scoped under .mly-nav and written
   with enough specificity to fully displace the admin template's sidebar
   rules — this owes nothing to the theme it replaced. Palette is the MilliyO
   brand read for a dark surface: milled-grain gold on deep forest. */
const MLY_NAV_CSS = `
.mly-nav{
  --f0:#0A1811; --f1:#102A1E;
  --gold:#C9952A; --gold-lt:#E8C96A;
  --cream:#F3EAD3; --muted:#8CA391; --faint:#5F7767;
  position:fixed; inset:0 auto 0 0; z-index:9;
  display:flex; height:100vh;
  font-family:Inter,system-ui,-apple-system,sans-serif;
}

/* ── the rail ─────────────────────────────────────────────────────────── */
.mly-nav .mly-rail{
  width:62px; flex:0 0 62px;
  display:flex; flex-direction:column; align-items:center;
  padding:12px 0 10px;
  background:var(--f0);
  border-right:1px solid #1C3C2B;
}
.mly-nav .mly-mark{
  width:40px; height:40px; border-radius:12px;
  display:flex; align-items:center; justify-content:center;
  background:#FDF6E3; margin-bottom:14px; flex:0 0 auto;
}
.mly-nav .mly-mark img{ width:30px; height:auto; display:block; }

.mly-nav .mly-sections{
  list-style:none; margin:0; padding:0;
  display:flex; flex-direction:column; gap:4px; width:100%;
  align-items:center; flex:1 1 auto; overflow-y:auto;
}
.mly-nav .mly-sections::-webkit-scrollbar{ width:0; }

.mly-nav .mly-sec{
  position:relative;
  width:46px; height:46px; border:0; padding:0;
  display:flex; flex-direction:column; align-items:center; justify-content:center;
  gap:2px; border-radius:13px; cursor:pointer;
  background:transparent; color:var(--faint);
  transition:background .15s ease, color .15s ease;
}
.mly-nav .mly-sec:hover{ color:var(--cream); background:#15372666; }
.mly-nav .mly-sec.is-on{ color:var(--f0); background:var(--gold); }
.mly-nav .mly-sec.is-on:hover{ background:var(--gold-lt); }
.mly-nav .mly-sec .mly-glyph{ width:21px; height:21px; }
.mly-nav .mly-sec b{
  font-size:7.5px; font-weight:800; letter-spacing:.06em; text-transform:uppercase;
}

.mly-nav .mly-rail-foot{ flex:0 0 auto; padding-top:8px; opacity:.5; }
.mly-nav .mly-rail-foot img{ width:26px; height:auto; display:block; }

/* ── the panel ────────────────────────────────────────────────────────── */
.mly-nav .mly-panel{
  width:212px; flex:0 0 212px;
  display:flex; flex-direction:column;
  background:var(--f1);
  border-right:1px solid #1C3C2B;
  overflow:hidden;
  transition:width .2s ease, flex-basis .2s ease;
}
.mly-nav.is-shut .mly-panel{ width:0; flex-basis:0; border-right:0; }

.mly-nav .mly-panel-head{
  display:flex; align-items:center; justify-content:space-between;
  gap:8px; padding:16px 14px 10px; flex:0 0 auto;
}
.mly-nav .mly-panel-head h2{
  margin:0; font-size:13px; font-weight:800; letter-spacing:.1em;
  text-transform:uppercase; color:var(--gold-lt); white-space:nowrap;
}
.mly-nav .mly-shut{
  width:26px; height:26px; border:0; border-radius:8px; cursor:pointer;
  display:flex; align-items:center; justify-content:center;
  background:transparent; color:var(--muted);
  transition:background .15s ease, color .15s ease;
}
.mly-nav .mly-shut:hover{ background:#163827; color:var(--cream); }

.mly-nav .mly-list{ list-style:none; margin:0; padding:0 8px 8px; }

.mly-nav .mly-link{
  position:relative; display:flex; align-items:center; gap:10px;
  padding:9px 10px; border-radius:10px; text-decoration:none;
  font-size:13.5px; font-weight:500; color:var(--cream);
  transition:background .14s ease, color .14s ease;
}
.mly-nav .mly-link:hover{ background:#17392866; }
.mly-nav .mly-item.is-active > .mly-link{
  background:#1B4531; color:#fff; font-weight:650;
}
/* The gold edge is what says "you are here" without relying on the green
   being distinguishable from the green behind it. */
.mly-nav .mly-item.is-active > .mly-link::before{
  content:""; position:absolute; left:-8px; top:50%; transform:translateY(-50%);
  width:3px; height:19px; border-radius:0 3px 3px 0; background:var(--gold);
}
.mly-nav .mly-link .mly-glyph{ width:17px; height:17px; flex:0 0 auto; opacity:.9; }
.mly-nav .mly-item.is-active > .mly-link .mly-glyph{ opacity:1; color:var(--gold-lt); }
.mly-nav .mly-label{ flex:1 1 auto; }

.mly-nav .mly-chev{
  display:flex; transition:transform .2s ease; color:var(--muted);
}
.mly-nav .mly-chev.is-open{ transform:rotate(90deg); }

.mly-nav .mly-sub{
  list-style:none; margin:0; padding:0 0 0 27px;
  overflow:hidden; transition:max-height .24s ease;
}
.mly-nav .mly-sub a{
  display:flex; align-items:center; gap:8px;
  padding:7px 10px; border-radius:9px; text-decoration:none;
  font-size:12.5px; color:var(--muted);
  transition:color .14s ease, background .14s ease;
}
.mly-nav .mly-sub a:hover{ color:var(--cream); background:#17392866; }
.mly-nav .mly-sub a.is-active{ color:var(--gold-lt); font-weight:650; }
.mly-nav .mly-dot{
  width:5px; height:5px; border-radius:50%; background:currentColor; opacity:.65;
  flex:0 0 auto;
}

.mly-nav .mly-foot{ flex:0 0 auto; padding:10px 14px 14px; }
.mly-nav .mly-foot-rule{ height:1px; background:#1C3C2B; margin-bottom:10px; }
.mly-nav .mly-foot-line{
  font-size:10px; font-weight:600; color:var(--muted); line-height:1.5;
  white-space:nowrap;
}
.mly-nav .mly-foot-line.is-gold{
  color:var(--gold); font-weight:800; letter-spacing:.08em; text-transform:uppercase;
  font-size:8.5px;
}

/* ── the body sits beside it ──────────────────────────────────────────── */
.page-wrapper.compact-wrapper .page-body-wrapper .page-body,
.page-wrapper.compact-wrapper .page-body-wrapper footer{
  margin-left:274px; transition:margin-left .2s ease;
}
html.mly-shut .page-wrapper.compact-wrapper .page-body-wrapper .page-body,
html.mly-shut .page-wrapper.compact-wrapper .page-body-wrapper footer{
  margin-left:62px;
}

@media (max-width:991px){
  .mly-nav{ transform:translateX(-100%); transition:transform .2s ease; }
  html.mly-open .mly-nav{ transform:none; }
  .page-wrapper.compact-wrapper .page-body-wrapper .page-body,
  .page-wrapper.compact-wrapper .page-body-wrapper footer{ margin-left:0; }
}

@media (prefers-reduced-motion:reduce){
  .mly-nav, .mly-nav *{ transition:none !important; }
}
`;

/* ─── Sections ───────────────────────────────────────────────────────────────
   The rail shows these, not the twenty-seven items. Each carries its own glyph
   rather than borrowing its first item's, so the rail stays stable when items
   move between sections. */
const SECTIONS = [
  { title: "Overview",  icon: "home",    keys: ["dashboard"] },
  { title: "Catalogue", icon: "package", keys: ["category", "subcategory", "products", "bulkallocate", "offer", "taxmaster", "gstreport"] },
  { title: "Shop",      icon: "mill",    keys: ["pos", "billdesign", "orders", "milling", "staff", "liveview", "stores", "storesettings"] },
  { title: "Growth",    icon: "award",   keys: ["customer", "loyalty", "banner", "branding", "notification", "emailsettings", "support"] },
  { title: "System",    icon: "shield",  keys: ["audit", "settings"] },
];

/* ─── The navigation ─────────────────────────────────────────────────────────
   ── WHY A RAIL AND A PANEL, AND NOT ONE LIST ──
   This used to be a single labelled column: every section heading and all
   twenty-seven items stacked down it, submenus opening as accordions in place.
   That is the shape the previous portal used, and painting it green did not
   change the shape — which is the whole of what someone recognises at a
   glance.

   Now the rail carries five section glyphs and nothing else, and the panel
   beside it holds only the section you are in. Three things fall out of that:
   the whole of a section is visible at once instead of being hunted for in a
   list of twenty-seven; the rail never scrolls or reflows, so the thing you
   aim at is always in the same place; and with the panel shut the navigation
   is 62px wide, which gives the wide tables back about 200px they were
   overflowing by.                                                           */
const Sidebar = () => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const userRole = useSelector((state) => state.adminUser.role);
  const { menu: adminMenu } = useSelector((state) => state.adminMenu);

  useEffect(() => {
    dispatch(fetchAdminMenu());
  }, [dispatch]);

  /* ── what this role may see ──────────────────────────────────────────────
     The rows /admin/menu returns are the matrix a superadmin saves under
     Settings → Permissions: one row per screen path, carrying the roles that
     may see it. An item with no row yet is visible to every role -- the
     default before anything was saved -- except the ones marked
     superAdminOnly, whose gate is the role rank and not this table. A
     superadmin sees everything regardless: the role that edits the matrix
     cannot lock itself out of it. */
  const menuItems = useMemo(() => {
    if (userRole == null) return [];
    const role = Number(userRole);
    const byPath = new Map((adminMenu || []).map((row) => [row.path, row]));
    return ALL_MENU_ITEMS.filter((item) => {
      if (item.superAdminOnly && role !== SUPERADMIN_ROLE) return false;
      if (role === SUPERADMIN_ROLE) return true;
      const row = byPath.get(item.path);
      return !row || (row.roles || []).map(Number).includes(role);
    });
  }, [adminMenu, userRole]);

  const isActive = (path, exact = false) =>
    exact ? pathname === path : pathname.startsWith(path);

  const isSubmenuActive = (item) => {
    if (!item.hasSubmenu) return false;
    const paths = item.activePaths || [item.path];
    return paths.some((p) => pathname.startsWith(p));
  };

  /* ── the sections, carrying only the items this role may see ── */
  const sections = useMemo(() => {
    const byKey = new Map(menuItems.map((i) => [i.key, i]));
    const used = new Set();
    const out = SECTIONS.map((s) => {
      const items = s.keys.map((k) => byKey.get(k)).filter(Boolean);
      items.forEach((i) => used.add(i.key));
      return { title: s.title, icon: s.icon, items };
    }).filter((s) => s.items.length);
    /* Anything not listed in SECTIONS still has to appear somewhere, or adding
       a screen and forgetting to file it makes the screen unreachable. */
    const rest = menuItems.filter((i) => !used.has(i.key));
    if (rest.length) out.push({ title: "More", icon: "sliders", items: rest });
    return out;
  }, [menuItems]);

  /* Which section the current URL belongs to. Derived rather than stored, so
     arriving by a bookmark or the browser's Back button opens the right
     panel. */
  const sectionOfPath = useMemo(() => {
    const at = sections.findIndex((s) =>
      s.items.some((i) =>
        i.hasSubmenu ? isSubmenuActive(i) : isActive(i.path, i.exact)
      )
    );
    return at < 0 ? 0 : at;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, pathname]);

  const [picked, setPicked] = useState(null);
  const current = sections[picked != null ? picked : sectionOfPath] || sections[0];

  /* Navigating hands control back to the URL — otherwise opening Catalogue to
     look at it, then following a link from the header, leaves the panel
     showing a section you are no longer in. */
  useEffect(() => {
    setPicked(null);
  }, [pathname]);

  const [shut, setShut] = useState(() => {
    try {
      return window.localStorage.getItem("mly-nav-shut") === "1";
    } catch {
      return false;
    }
  });

  /* The class goes on <html> so the page body's margin can answer it without
     the two components having to know about each other. */
  useEffect(() => {
    document.documentElement.classList.toggle("mly-shut", shut);
    try {
      window.localStorage.setItem("mly-nav-shut", shut ? "1" : "0");
    } catch {
      /* private window — the preference just will not persist */
    }
  }, [shut]);

  /* Which submenu is open inside the panel. */
  const [openKey, setOpenKey] = useState(null);
  useEffect(() => {
    const hit = menuItems.find((i) => i.hasSubmenu && isSubmenuActive(i));
    setOpenKey(hit ? hit.key : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, menuItems]);

  /* ── before there is anything to show ──
     A failed /admin/menu does NOT replace the navigation with an error panel.
     The items are defined in this file; that call supplies only an optional
     permission map, so losing it should cost the filtering, not the whole
     sidebar — an operator who cannot navigate cannot work. */
  if (!current) {
    return (
      <div className="sidebar-wrapper mly-nav">
        <style>{MLY_NAV_CSS}</style>
        <nav className="mly-rail">
          <span className="mly-mark">
            <img src={logo} alt="MilliyO" />
          </span>
        </nav>
      </div>
    );
  }

  const renderItem = (item) => {
    const active = item.hasSubmenu
      ? isSubmenuActive(item)
      : isActive(item.path, item.exact);
    const expanded = openKey === item.key;

    return (
      <li key={item.key} className={active ? "mly-item is-active" : "mly-item"}>
        {!item.hasSubmenu ? (
          <Link className="mly-link" to={item.path}>
            <PortalIcon name={item.icon} size={17} />
            <span className="mly-label">{item.title}</span>
          </Link>
        ) : (
          <>
            <button
              type="button"
              className="mly-link"
              style={{
                width: "100%",
                border: 0,
                background: "transparent",
                font: "inherit",
                cursor: "pointer",
                textAlign: "left",
              }}
              onClick={() => setOpenKey(expanded ? null : item.key)}
              aria-expanded={expanded}
            >
              <PortalIcon name={item.icon} size={17} />
              <span className="mly-label">{item.title}</span>
              <span className={expanded ? "mly-chev is-open" : "mly-chev"}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M9.4 18.6a1.2 1.2 0 0 1 0-1.7l4.9-4.9-4.9-4.9a1.2 1.2 0 0 1 1.7-1.7l5.7 5.7a1.2 1.2 0 0 1 0 1.7l-5.7 5.7a1.2 1.2 0 0 1-1.7 0Z" />
                </svg>
              </span>
            </button>
            <ul className="mly-sub" style={{ maxHeight: expanded ? 320 : 0 }}>
              {item.subItems.map((sub, idx) => (
                <li key={idx}>
                  <Link
                    to={sub.path}
                    className={isActive(sub.path, sub.exact) ? "is-active" : ""}
                  >
                    <span className="mly-dot" />
                    {sub.title}
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}
      </li>
    );
  };

  return (
    <div className={shut ? "sidebar-wrapper mly-nav is-shut" : "sidebar-wrapper mly-nav"}>
      <style>{MLY_NAV_CSS}</style>

      {/* ── the rail: one glyph per section, never scrolls, never reflows ── */}
      <nav className="mly-rail" aria-label="Sections">
        <span className="mly-mark">
          <img src={logo} alt="MilliyO" />
        </span>

        <ul className="mly-sections">
          {sections.map((s, i) => {
            const on = s.title === current.title;
            return (
              <li key={s.title}>
                <button
                  type="button"
                  className={on ? "mly-sec is-on" : "mly-sec"}
                  title={s.title}
                  aria-current={on ? "true" : undefined}
                  onClick={() => {
                    setPicked(i);
                    if (shut) setShut(false);
                  }}
                >
                  <PortalIcon name={s.icon} size={21} />
                  <b>{s.title.slice(0, 4)}</b>
                </button>
              </li>
            );
          })}
        </ul>

        <div className="mly-rail-foot">
          <img src={sentrioLogo} alt="Sentrio" />
        </div>
      </nav>

      {/* ── the panel: only the section you are in ── */}
      <div className="mly-panel">
        <div className="mly-panel-head">
          <h2>{current.title}</h2>
          <button
            type="button"
            className="mly-shut"
            onClick={() => setShut(true)}
            title="Hide the panel"
            aria-label="Hide the panel"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M14.6 5.4a1.2 1.2 0 0 1 0 1.7L9.7 12l4.9 4.9a1.2 1.2 0 0 1-1.7 1.7l-5.7-5.7a1.2 1.2 0 0 1 0-1.7l5.7-5.7a1.2 1.2 0 0 1 1.7 0Z" />
            </svg>
          </button>
        </div>

        <SimpleBar autoHide style={{ flex: "1 1 auto", minHeight: 0 }}>
          <ul className="mly-list">{current.items.map(renderItem)}</ul>
        </SimpleBar>

        <div className="mly-foot">
          <div className="mly-foot-rule" />
          <div className="mly-foot-line">Into Today&#39;s Kitchen</div>
          <div className="mly-foot-line is-gold">For Tomorrow&#39;s Generation</div>
        </div>
      </div>
    </div>
  );
};

/* The permissions page builds its matrix from the same list and the same
   sections, so a screen added here appears there without a second edit. */
export { ALL_MENU_ITEMS, SECTIONS };
export default Sidebar;
